import type { Table, TableProduct } from '../types';

export function mergeUpdatedTable(existingTable: Table, updatedTable: Table): Table {
  const merged = { ...existingTable, ...updatedTable };
  if (!updatedTable.color) {
    delete merged.color;
  }
  return merged;
}

export function cloneTable(table: Table): Table {
  return {
    ...table,
    products: Array.isArray(table.products) ? table.products.map((product) => ({ ...product })) : [],
  };
}

function areTableProductsEqual(leftProducts?: TableProduct[], rightProducts?: TableProduct[]): boolean {
  const left = leftProducts ?? [];
  const right = rightProducts ?? [];

  if (left.length !== right.length) {
    return false;
  }

  return left.every((product, index) => {
    const candidate = right[index];
    return (
      product.id === candidate?.id &&
      product.itemId === candidate?.itemId &&
      product.name === candidate?.name &&
      (product.unitPrice ?? null) === (candidate?.unitPrice ?? null) &&
      (product.note ?? '') === (candidate?.note ?? '') &&
      product.qty === candidate?.qty &&
      product.handling === candidate?.handling &&
      product.delivered === candidate?.delivered &&
      product.paid === candidate?.paid
    );
  });
}

export function areTablesEqual(left: Table | null | undefined, right: Table | null | undefined): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.id === right.id &&
    left.name === right.name &&
    left.x === right.x &&
    left.y === right.y &&
    left.w === right.w &&
    left.h === right.h &&
    (left.person ?? '') === (right.person ?? '') &&
    (left.color ?? '') === (right.color ?? '') &&
    areTableProductsEqual(left.products, right.products)
  );
}

export function resolveTableColor(table: Table, defaultTableColor: string): string {
  return table.color || defaultTableColor;
}

export function getTableProductStateClass(table: Table): string {
  const products = table.products ?? [];
  if (products.length === 0) {
    return '';
  }

  const hasUnaddressedProduct = products.some((product) => !product.handling && !product.delivered);
  if (hasUnaddressedProduct) {
    return 'hasProducts';
  }

  const hasHandlingProduct = products.some((product) => product.handling && !product.delivered);
  if (hasHandlingProduct) {
    return 'hasProducts allProductsHandling';
  }

  const allDelivered = products.every((product) => product.delivered);
  if (allDelivered) {
    return 'hasProducts allProductsDelivered';
  }

  return 'hasProducts';
}
